import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowDownLeftIcon, PlaneIcon, ReceiptIcon } from 'lucide-react';
import type { WalletTransaction } from '../../types';
import { formatNaira } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/dateUtils';
import { StatusBadge } from '../ui/StatusBadge';
export interface TransactionListProps {
  transactions: WalletTransaction[];
  onTransactionClick?: (transaction: WalletTransaction) => void;
  showReceipt?: boolean;
  className?: string;
}
export function TransactionList({
  transactions,
  onTransactionClick,
  showReceipt = true,
  className = ''
}: TransactionListProps) {
  if (transactions.length === 0) {
    return <div className="text-center py-12" data-id="element-174">
        <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 rounded-full flex items-center justify-center" data-id="element-175">
          <ReceiptIcon className="w-8 h-8 text-surface-400" data-id="element-176" />
        </div>
        <p className="text-body-md text-surface-600" data-id="element-177">No transactions yet</p>
        <p className="text-body-sm text-surface-500 mt-1" data-id="element-178">
          Get a refund to start building your store credit
        </p>
      </div>;
  }
  return <div className={`divide-y divide-surface-100 ${className}`} data-id="element-179">
      {transactions.map((transaction, index) => <motion.div key={transaction.id} initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.05
    }} onClick={() => onTransactionClick?.(transaction)} className={`
            flex items-center gap-4 py-4
            ${onTransactionClick ? 'cursor-pointer hover:bg-surface-50 -mx-4 px-4 transition-colors' : ''}
          `} data-id="element-180">
          {/* Icon */}
          <div className={`
              w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
              ${transaction.type === 'credit' ? 'bg-success-100 text-success-600' : 'bg-surface-100 text-surface-600'}
            `} data-id="element-181">
            {transaction.type === 'credit' ? <ArrowDownLeftIcon className="w-5 h-5" data-id="element-182" /> : <ArrowUpRightIcon className="w-5 h-5" data-id="element-183" />}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0" data-id="element-184">
            <div className="flex items-center gap-2" data-id="element-185">
              <p className="text-body-md text-surface-900 font-medium truncate" data-id="element-186">
                {transaction.description}
              </p>
              {transaction.metadata?.flightNumber && <span className="flex items-center gap-1 text-caption text-surface-500" data-id="element-187">
                  <PlaneIcon className="w-3 h-3" data-id="element-188" />
                  {transaction.metadata.flightNumber}
                </span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-189">
              <span className="text-body-sm text-surface-500" data-id="element-190">
                {formatDate(transaction.createdAt, {
              includeTime: true
            })}
              </span>
              <StatusBadge status={transaction.status === 'completed' ? 'complete' : transaction.status === 'pending' ? 'pending' : 'failed'} size="sm" data-id="element-191">
                {transaction.status}
              </StatusBadge>
            </div>
          </div>

          {/* Amount */}
          <div className="text-right flex-shrink-0" data-id="element-192">
            <p className={`
                text-body-md font-semibold tabular-nums
                ${transaction.type === 'credit' ? 'text-success-600' : 'text-surface-900'}
              `} data-id="element-193">
              {transaction.type === 'credit' ? '+' : '-'}
              {formatNaira(transaction.amount)}
            </p>
            {showReceipt && transaction.status === 'completed' && <button onClick={e => {
          e.stopPropagation();
          // Handle receipt download
        }} className="text-caption text-primary-500 hover:text-primary-600 mt-0.5" data-id="element-194">
                Receipt
              </button>}
          </div>
        </motion.div>)}
    </div>;
}